"use client";

import React, { useState } from "react";
import { ScanSearch, DraftingCompass, Rocket, Activity, ArrowRight, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "Definizione dell’architettura infrastrutturale target",
  shortDesc: "Progettazione dell’assetto tecnologico futuro, ottimizzato per le specifiche esigenze di business e pronto a supportare la crescita aziendale con soluzioni scalabili e sicure.",
  icon: ScanSearch,
  details: {
    heading: "Audit Tecnico Completo",
    points: ["Audit completo dell'infrastruttura hardware e software", "Analisi delle performance e dei carichi di lavoro attuali", "Identificazione di criticità, rischi e colli di bottiglia", "Verifica della conformità e della sicurezza dei sistemi"]
  }
}, {
  id: 2,
  title: "Progettazione della rete e delle componenti di sistema",
  shortDesc: "Pianificazione dettagliata dell’infrastruttura di rete e dei sistemi core, assicurando prestazioni elevate, ridondanza e una gestione centralizzata delle risorse tecnologiche.",
  icon: DraftingCompass,
  details: {
    heading: "Architettura & Strategia",
    points: ["Progettazione di architetture di rete e sistemi scalabili", "Definizione di standard tecnici e policy di sicurezza", "Pianificazione di strategie di ridondanza e backup", "Selezione di hardware e software basata sui requisiti"]
  }
}, {
  id: 3,
  title: "Strategia di integrazione con sistemi esistenti",
  shortDesc: "Sviluppo di piani di migrazione e integrazione per far coesistere le nuove soluzioni con l’ambiente IT attuale, garantendo la continuità dei flussi di dati e dei processi operativi.",
  icon: Rocket,
  details: {
    heading: "Deployment & Migrazione",
    points: ["Installazione e configurazione delle componenti infrastrutturali", "Deployment delle soluzioni tecnologiche in ambiente operativo", "Migrazione controllata di sistemi, servizi e dati", "Verifica tecnica e stabilizzazione dell’ambiente IT"]
  }
}, {
  id: 4,
  title: "Linee guida per continuità operativa e scalabilità",
  shortDesc: "Definizione di standard tecnici e procedure per assicurare l’affidabilità dei sistemi nel tempo e la loro capacità di espandersi in modo efficiente seguendo l’evoluzione dell’attività.",
  icon: Activity,
  details: {
    heading: "Supporto Proattivo",
    points: ["Setup di sistemi di monitoraggio e alerting proattivo", "Definizione di piani di manutenzione e aggiornamento", "Supporto tecnico specialistico per l'ottimizzazione", "Analisi periodica per l'evoluzione dell'infrastruttura"]
  }
}];
export default function IlMetodoBTech() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  return <section id="il-metodo-b-tech" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-primary font-mono text-sm tracking-wider uppercase mb-3">
            Workflow Operativo
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-white">
            Il Metodo B-Tech
          </h3>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Installazione e configurazione delle componenti infrastrutturali<br />
            Deployment delle soluzioni tecnologiche in ambiente operativo<br />
            Migrazione controllata di sistemi, servizi e dati<br />
            Verifica tecnica e stabilizzazione dell’ambiente IT
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {}
          <div className="hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-border/30 -z-10" />

          {steps.map((step, index) => {
          const isHovered = hoveredStep === step.id;
          const isDimmed = hoveredStep !== null && hoveredStep !== step.id;
          return <div key={step.id} data-index={index} className={`transition-all duration-500 ease-out ${isDimmed ? 'opacity-40 scale-95 blur-[1px]' : 'opacity-100 scale-100 blur-0'}`} onMouseEnter={() => setHoveredStep(step.id)} onMouseLeave={() => setHoveredStep(null)}>
                <HoverCard openDelay={0} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <div className="h-full cursor-pointer group">
                      <Card className="h-full bg-card/40 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_-10px_var(--color-primary)] hover:-translate-y-1 relative overflow-hidden">
                        {}
                        <div className="absolute -right-4 -top-4 text-9xl font-bold text-foreground/5 opacity-[0.03] select-none pointer-events-none group-hover:text-primary/10 transition-colors">
                          {step.id}
                        </div>

                        <CardHeader className="pb-4 relative">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 border border-primary/20 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                            <step.icon className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-primary/80 bg-primary/10 px-2 py-0.5 rounded-full border border-primary/10">
                              Fase 0{step.id}
                            </span>
                          </div>
                          <CardTitle className="text-xl text-white group-hover:text-primary transition-colors">
                            {step.title}
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent>
                          <CardDescription className="text-muted-foreground leading-relaxed">
                            {step.shortDesc}
                          </CardDescription>
                          
                          <div className="mt-6 flex items-center text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
                            Dettagli tecnici <ArrowRight className="w-4 h-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </HoverCardTrigger>
                  
                  <HoverCardContent side="top" align="center" className="w-80 bg-popover/95 backdrop-blur-xl border-primary/20 p-0 overflow-hidden shadow-2xl shadow-black/50">
                    <div className="bg-primary/10 p-4 border-b border-primary/10">
                      <h4 className="font-semibold text-primary flex items-center gap-2">
                        <step.icon className="w-4 h-4" />
                        {step.details.heading}
                      </h4>
                    </div>
                    <div className="p-4">
                      <ul className="space-y-3">
                        {step.details.points.map((point, i) => <li key={i} data-index={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <CircleCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]">
            <Link to="/ContactsPage">
              Richiedi una valutazione dell’infrastruttura IT
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}